import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { LeftOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Grid,
  Input,
  message,
  Row,
  Select,
  Typography,
  Upload,
  Card,
  Space,
} from 'antd';
import type {
  UploadFile,
  UploadProps,
} from 'antd/es/upload/interface';
import { LoadingOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import Layout from '../../../layouts/User';
import Marker, { Location } from '../../../components/Marker';
import styles from '../index.module.scss';
import UploadService from '../../../services/upload';

import {
  validatePhoneNumber,
  validateNumberOnly,
} from '../../../functions';

import {
  ORDER_SERVICE_CATEGORY,
  sendTechnicianNotiMessage,
} from '../../../reducers/actions/orderService';
import {
  getPostalCodes,
  getCities,
  getSubCities,
  getStates,
} from '../../../services/api';
import TechnicianCard from '../Listing/technicianCard';
import Step from '../step';
import AddressSelection from '../../../components/AddressSelection';

type MarkerLocation = Location;

interface ImageURL {
  image_url: string;
  _id: string;
}

interface CreateServiceJob {
  userId: string;
  applyJobId: string;
  description: string;
  status: string;
  images: object[];
  name: string;
  phone: string;
  address: string;
  subDistrict: string;
  district: string;
  postCode: string;
  province: string;
}

interface Customer {
  name: string;
  phone: string;
  address: string;
  subDistrict: string;
  district: string;
  province: string;
  postCode: string;
}

interface CreatedServiceJob {
  userId: string;
  applyJobId: string;
  description: string;
  status: string;
  images: ImageURL[];
  customer: Customer;
  category: string;
}

interface ProvinceOption {
  label: string;
  value: string;
}

interface LocationState {
  category: string;
}

const CONSTRUCTION = {
  CREATED: 'created',
};

enum CATEGORY {
  'electrical' = 'ระบบไฟฟ้า',
  'water' = 'ระบบประปา',
  'floor' = 'งานปูน/พื้น/กระเบื้อง',
  'aluminium' = 'งานเหล็ก/อลูมิเนียม/ไม้',
  'ceiling' = 'งานฝ้า/หลังคา',
  'paint' = 'งานทาสี',
  'contractor' = 'ผู้รับเหมา',
  'architect' = 'ออกแบบ/สถาปนิก',
}

interface Category {
  label: string;
  value: string;
}

const categories = [
  {
    label: 'ระบบไฟฟ้า',
    value: 'electrical',
  },
  {
    label: 'ระบบประปา',
    value: 'water',
  },
  {
    label: 'งานปูน/พื้น/กระเบื้อง',
    value: 'floor',
  },
  {
    label: 'งานเหล็ก/อลูมิเนียม/ไม้',
    value: 'aluminium',
  },
  {
    label: 'งานฝ้า/หลังคา',
    value: 'ceiling',
  },
  {
    label: 'งานทาสี',
    value: 'paint',
  },
  {
    label: 'ผู้รับเหมา',
    value: 'contractor',
  },
  {
    label: 'ออกแบบ/สถาปนิก',
    value: 'architect',
  },
];

export type Props = {};

const ServiceContact: React.FC<Props> = (props) => {
  const { Text } = Typography;
  const { id } = useParams();
  const { Option } = Select;
  const [form] = Form.useForm();
  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const location = useLocation();
  const state = location.state as LocationState;
  const { category } = state;
  const navigate = useNavigate();

  const [blockButton, setBlockbutton] = useState(false);
  const [job, setJob] = useState({});
  const [province, setProvince] = useState('');
  const [categoryData, setCategoryData] = useState('');
  const [provinces, setProvinces] = useState<ProvinceOption[]>([]);
  const [currentLocation, setCurrentLocation] = useState({
    lat: 0,
    long: 0,
  });
  const [createSuccess, setCreateSuccess] = useState(false);
  const [createdJob, setCreatedJob] = useState<CreatedServiceJob>({
    userId: '',
    applyJobId: '',
    description: '',
    status: '',
    images: [],
    customer: {
      name: '',
      phone: '',
      address: '',
      subDistrict: '',
      district: '',
      province: '',
      postCode: '',
    },
    category: '',
  });
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const user = useSelector((state: any) => state.user);
  const currentAddress = useSelector(
    (state: any) => state.currentAddress
  );
  const currentLat = useSelector(() =>
    currentAddress?.geometry?.location?.lat()
  );
  const currentLong = useSelector(() =>
    currentAddress?.geometry?.location?.long()
  );
  const isLoadaing = useSelector(() => loading || isUploading);

  const [postalCodes, setPostalCodes] = useState<any>([]);
  const [subCities, setSubCities] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [states, setStates] = useState<any>([]);

  const useQuery = () => {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  };
  const query = useQuery();

  useEffect(() => {
    const province = query.get('province');
    form.setFieldsValue({ province });
  }, [query]);

  const getCategoryData = async () => {
    let target = categories.find(
      (catOptions: Category) => catOptions.value === category
    );
    setCategoryData(target?.label!);
  };

  const onImageChange: UploadProps['onChange'] = async ({
    fileList,
  }) => {
    setFileList(fileList);
  };

  const uploaderProps: UploadProps = {
    listType: 'picture-card',
    fileList: fileList,
    onChange: (e) => {
      onImageChange(e);
    },
    maxCount: 15,
    multiple: true,
    beforeUpload: (file) => {
      const isImgFile =
        file.type === 'image/png' || file.type === 'image/jpeg';
      // || file.type === 'video/mp4';
      if (!isImgFile) {
        message.error(
          `${file.name} ฟอร์แมตไม่ถูกต้อง\nกรุณาอัพโหลดไฟล์​ .png หรือ .jpg`
        );
      }
      return isImgFile || Upload.LIST_IGNORE;
    },
    customRequest: ({ onSuccess }: any) => {
      onSuccess();
    },
  };

  const handleContactClick = async (values: CreateServiceJob) => {
    const {
      description,
      name,
      phone,
      address,
      subDistrict,
      district,
      postCode,
      province,
    } = values;

    let images: Array<object> = [];
    try {
      setIsUploading(true);
      const imageUrls = await UploadService.uploadFile(fileList);
      images = imageUrls.map((imageUrl: string) => {
        return { image_url: imageUrl };
      });
    } catch (error: any) {
      message.error(error?.message);
    } finally {
      setIsUploading(false);
    }

    const body = {
      userId: user?._id,
      applyJob: id,
      images,
      description,
      customer: {
        name,
        phone,
        address,
        subDistrict,
        district,
        postCode,
        province,
        lat: currentLocation.lat,
        lng: currentLocation.long,
      },
      category,
    };

    try {
      setLoading(true);
      const orderApplyJob = await getOrderApplyJob(body?.applyJob);
      const res = await axios.post('/order-service-jobs', body);
      const { status, data } = res;
      if (status === 200) {
        sendTechnicianNotiMessage(
          CONSTRUCTION.CREATED,
          orderApplyJob?.applyJob?.user,
          categoryData,
          states[0]?.label,
          data?.data?.orderNo
        );
        message.success('แจ้งรายละเอียดงานสำเร็จ');
        setCreatedJob({
          ...data?.data,
        });
        setCreateSuccess(true);
      }
    } catch (error: any) {
      message.error(error?.message);
    } finally {
      setLoading(false);
      navigate(`/profile/service-orders?status=CREATED`);
    }
  };

  const getOrderApplyJob = async (id: any) => {
    const { data } = await axios.get(`/applyJobs/${id}`);
    return data;
  };

  const handleDragEnd = (position: any) => {
    setCurrentLocation(position);
  };

  const initialPostalCode = async () => {
    setPostalCodes(await getPostalCodes());
  };

  useEffect(() => {
    let isBlock =
      breakpoints.xs ||
      ((breakpoints.sm || breakpoints.md) && !breakpoints.lg) ||
      false;
    setBlockbutton(isBlock);
  }, [breakpoints]);

  useEffect(() => {
    getCategoryData();
    initialPostalCode();
    setCurrentLocation({
      lat: currentLat,
      long: currentLong,
    });
  }, []);

  useEffect(() => {
    getOrderApplyJob(id).then((data) => {
      setJob(data.applyJob);
    });
  }, [id]);

  const CreatedJobDetail = () => {
    return (
      <>
        <div className={styles.header}>รายละเอียดงาน</div>
        <div>
          <Row style={{ marginBottom: '1rem' }}>
            <Col xs={24} sm={6}>
              <Text strong>ชื่อ</Text>
            </Col>
            <Col xs={24} sm={18}>
              <Text>{createdJob.customer.name}</Text>
            </Col>
          </Row>

          <Row style={{ marginBottom: '1rem' }}>
            <Col xs={24} sm={6}>
              <Text strong>ที่อยู่</Text>
            </Col>
            <Col xs={24} sm={18}>
              <Text>{createdJob.customer.address} </Text>
              <Text>{createdJob.customer.subDistrict} </Text>
              <Text>{createdJob.customer.district} </Text>
              <Text>{createdJob.customer.province} </Text>
              <Text>{createdJob.customer.postCode}</Text>
            </Col>
          </Row>

          <Row style={{ marginBottom: '1rem' }}>
            <Col xs={24} sm={6}>
              <Text strong>เบอร์โทรศัพท์</Text>
            </Col>
            <Col xs={24} sm={18}>
              <Text>{createdJob.customer.phone}</Text>
            </Col>
          </Row>

          <Row style={{ marginBottom: '1rem' }}>
            <Col xs={24} sm={6}>
              <Text strong>รายละเอียดงาน</Text>
            </Col>
            <Col xs={24} sm={18}>
              <Text>{createdJob.description || '-'}</Text>
            </Col>
          </Row>

          <Row style={{ marginBottom: '1rem' }}>
            <Col xs={24} sm={6}>
              <Text strong>รูปหน้างาน</Text>
            </Col>
            <Col xs={24} sm={18}>
              {createdJob.images.length ? (
                <>
                  {Object.values(createdJob.images).map(
                    (image: ImageURL, i: number) => (
                      <img
                        className={styles.jobImage}
                        src={image.image_url}
                        key={`j-img-${i}`}
                      />
                    )
                  )}
                </>
              ) : (
                <Text>-</Text>
              )}
            </Col>
          </Row>

          <div style={{ marginTop: '5rem' }}>
            <Link to="/">
              <Button size="large" type="primary" block={blockButton}>
                กลับหน้าหลัก
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Layout>
        <div style={{ paddingTop: '16px' }}>
          <Button type="link" onClick={() => navigate(-1)}>
            <LeftOutlined /> ย้อนกลับ
          </Button>
          <h1></h1>
        </div>
        <div style={{ paddingTop: '16px' }}>
          <Step current={2} />
          <br />
          <TechnicianCard job={job} />
          {isLoadaing ? (
            <div style={{ textAlign: 'center' }}>
              <LoadingOutlined className={styles.loadingIcon} />
            </div>
          ) : (
            <>
              {createSuccess ? (
                <CreatedJobDetail />
              ) : (
                <>
                  <Card style={{ width: '100%' }} bordered={false}>
                    <Form
                      name="contact"
                      form={form}
                      layout="vertical"
                      onFinish={handleContactClick}
                    >
                      <Form.Item
                        name="name"
                        label="ชื่อผู้ติดต่อ"
                        rules={[
                          {
                            required: true,
                            message: 'กรุณากรอกข้อมูล',
                          },
                        ]}
                      >
                        <Input placeholder="ชื่อผู้ติดต่อ" />
                      </Form.Item>

                      <Form.Item
                        name="phone"
                        label="เบอร์โทรศัพท์"
                        rules={[
                          {
                            required: true,
                            validator: validatePhoneNumber,
                          },
                        ]}
                      >
                        <Input
                          placeholder="เบอร์โทรศัพท์"
                          onKeyPress={(event) =>
                            validateNumberOnly(event)
                          }
                          maxLength={10}
                        />
                      </Form.Item>

                      <Form.Item
                        name="address"
                        label="ที่อยู่"
                        rules={[
                          {
                            required: true,
                            message: 'กรุณากรอกข้อมูล',
                          },
                        ]}
                      >
                        <Input.TextArea placeholder="ที่อยู่" />
                      </Form.Item>

                      <AddressSelection
                        province={query.get('province') || undefined}
                        disabledProvince={true}
                      />

                      <Form.Item
                        name="description"
                        label="รายละเอียดเพิ่มเติม (ถ้ามี)"
                      >
                        <Input.TextArea placeholder="รายละเอียดเพิ่มเติม" />
                      </Form.Item>

                      <Form.Item
                        name="customerImages"
                        label="รูปหน้างาน/แบบแปลน (ถ้ามี)"
                      >
                        <Upload {...uploaderProps}>
                          {fileList.length < 15 && '+ Upload'}
                        </Upload>
                      </Form.Item>
                      <div>
                        <div style={{ fontSize: 14 }}>
                          พิกัดสถานที่
                        </div>
                        <div>
                          <Marker
                            current={currentLocation}
                            locationState={(
                              position: MarkerLocation
                            ) => handleDragEnd(position)}
                            style={{ height: 450 }}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: '5rem',
                          textAlign: 'end',
                        }}
                      >
                        <Space wrap>
                          <Button
                            size="large"
                            style={{ width: '150px' }}
                            htmlType="submit"
                            block={blockButton}
                            onClick={() => navigate(-1)}
                          >
                            ยกเลิก
                          </Button>
                          <Button
                            size="large"
                            type="primary"
                            htmlType="submit"
                            block={blockButton}
                          >
                            ยืนยันติดต่อช่าง
                          </Button>
                        </Space>
                      </div>
                    </Form>
                  </Card>
                </>
              )}
            </>
          )}
        </div>
      </Layout>
    </>
  );
};

export default ServiceContact;
